import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getPageTitle } from "../../utils/getPageTitle";
import Button from "../../components/common/button";
import ClientData from "../../components/clientData";
import ClientModal from "../../components/modal/clientModal";
import { useViewClientQuery } from "../../hooks/clients/viewClientList";
import { useClientStore } from "../../hooks/clients/createClient";
import { useClientUpdate } from "../../hooks/clients/editClient";
import { useDeleteClient } from "../../hooks/clients/deleteClient";
import { useQueryClient } from "@tanstack/react-query";
import SearchItem from "../../components/searchItem";
import Pagination from "../../components/common/pagination";
import DeleteModal from "../../components/modal/deleteModal";
import "../../assets/styles/products.css";
import "../../assets/styles/pagination.css";

const Clients = () => {
  const location = useLocation();
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [mode, setMode] = useState("create");
  const [localClients, setLocalClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalDocs: 0,
    limit: pageSize,
    hasNextPage: false,
    hasPrevPage: false,
  });

  const {
    data: clientsResponse,
    isLoading,
    isFetching,
    isError,
  } = useViewClientQuery({
    page,
    limit: pageSize,
    q: debouncedSearchTerm,
  });
  const { mutate: addClient, isPending: isAdding } = useClientStore();
  const { mutate: updateClient, isPending: isUpdating } = useClientUpdate();
  const { mutate: deleteClient, isPending: isDeleting } = useDeleteClient();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (!clientsResponse) return;
    setLocalClients(clientsResponse.clients ?? []);
    const apiPagination = clientsResponse.pagination;
    if (apiPagination) {
      setPagination({
        currentPage: apiPagination.currentPage || 1,
        totalPages: apiPagination.totalPages || 1,
        totalDocs: apiPagination.total || 0,
        limit: apiPagination.limit || pageSize,
        hasNextPage: apiPagination.hasNextPage || false,
        hasPrevPage: apiPagination.hasPrevPage || false,
      });
    }
  }, [clientsResponse, pageSize]);

  const handlePageChange = (newPage) => setPage(newPage);

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(1);
  };

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  const handleEdit = (client) => {
    setSelectedClient(client);
    setMode("edit");
    setIsModalOpen(true);
  };

  const handleDelete = (client) => {
    setSelectedClient(client);
    setMode("delete");
    setIsModalOpen(true);
  };

  const handleAddClient = () => {
    setSelectedClient(null);
    setMode("create");
    setIsModalOpen(true);
  };

  const handleSave = (data) => {
    const mutationOptions = {
      onSuccess: () => {
        setIsModalOpen(false);
        setSelectedClient(null);
        queryClient.invalidateQueries({ queryKey: ["viewClient"] });
      },
      onError: (err) => {
        alert(
          `Operation failed: ${
            err.response?.data?.message || err.message || "Please try again."
          }`,
        );
      },
    };

    if (mode === "edit" && selectedClient) {
      const id = selectedClient._id || selectedClient.id;
      updateClient({ id, body: data }, mutationOptions);
    } else if (mode === "delete" && selectedClient) {
      const id = selectedClient._id || selectedClient.id;
      deleteClient(id, mutationOptions);
    } else {
      addClient(data, mutationOptions);
    }
  };

  if (isLoading) return <div>Loading...</div>;
  if (isError) return <div>Error loading clients</div>;

  return (
    <>
      <div className="page-body">
        <div className="page-header">
          <h1 className="page-title">{getPageTitle(location.pathname)}</h1>
          <p style={{ color: "rgb(85, 85, 85)", fontSize: "13px" }}>
            Manage your client relationships
          </p>
        </div>
        <Button onClick={handleAddClient}>+ Add Client</Button>
      </div>

      <div className="list-toolbar">
        <div className="list-toolbar-row">
          <SearchItem
            data={localClients}
            searchField={["name", "email", "phone", "country"]}
            placeholder="Search clients..."
            wrapSection={false}
            onResultsChange={({ searchItem }) => setSearchTerm(searchItem)}
          />
          <label className="list-toolbar-page-size">
            <span>Items per page:</span>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={15}>15</option>
              <option value={20}>20</option>
            </select>
          </label>
        </div>
      </div>

      <ClientModal
        isOpen={isModalOpen && mode !== "delete"}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedClient(null);
        }}
        client={selectedClient}
        mode={mode}
        onSave={handleSave}
        isLoading={isAdding || isUpdating}
      />

      <DeleteModal
        isOpen={isModalOpen && mode === "delete"}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedClient(null);
        }}
        onConfirm={() => handleSave()}
        title="Delete Client"
        itemName={selectedClient?.name}
        isLoading={isDeleting}
      />

      <ClientData
        data={localClients}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      {isFetching && !isLoading && (
        <p style={{ fontSize: 12, color: "#888", marginTop: 8 }}>Updating...</p>
      )}

      <Pagination
        currentPage={page}
        totalPages={pagination.totalPages}
        onPageChange={handlePageChange}
      />
    </>
  );
};

export default Clients;
